import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query'

import type { TPrayersApi, TThanksMessageType } from '@/src/features/intention/api/types/getIntentionPrayersApi.types'
import { getIntentionPrayersOptions } from '@/src/features/intention/services/useIntentionPrayersQuery'
import { messageApi } from '@/src/features/message/api/messageApi'
import type { TCreateMessagePayload } from '@/src/features/message/api/types/createMessageApi.types'
import { messageTypePrayerTypeMap } from '@/src/features/message/models/messageTypePrayerTypeMap'

import { MESSAGE_TYPE } from '../models/Message.enums'

type TCreateThankMessagePayload = TCreateMessagePayload<keyof typeof messageTypePrayerTypeMap>

type TPrayerUpdaterParams = {
  data: InfiniteData<TPrayersApi[]> | undefined
  prayerId: number
  type: TThanksMessageType
  response?: { conversationId: number }
}

export const prayerUpdater = ({
  data,
  prayerId,
  type,
  response,
}: TPrayerUpdaterParams): InfiniteData<TPrayersApi[]> | undefined => {
  if (!data) return data
  for (const [pageIndex, page] of data.pages.entries()) {
    for (const [intentionPrayerIndex, intentionPrayer] of page.entries()) {
      if (intentionPrayer.id === prayerId) {
        const updatedPage = [
          ...page.slice(0, intentionPrayerIndex),
          {
            ...intentionPrayer,
            messages: [
              {
                type,
                conversation: response?.conversationId ? { id: response?.conversationId } : undefined,
              },
            ],
          },
          ...page.slice(intentionPrayerIndex + 1),
        ]
        return {
          ...data,
          pages: [...data.pages.slice(0, pageIndex), updatedPage, ...data.pages.slice(pageIndex + 1)],
        }
      }
    }
  }
  return data
}

export const useCreateThankMessageMutation = ({
  intentionId,
  type,
}: { intentionId: number } & Omit<TCreateThankMessagePayload, 'prayerId' | 'users'>) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ prayerId, userId }: { prayerId: number; userId: number }) =>
      messageApi.createMessage({ type, prayerId, users: [userId] }),
    onSuccess: (response, { prayerId }) => {
      queryClient.setQueryData(
        getIntentionPrayersOptions(intentionId, messageTypePrayerTypeMap[type]).queryKey,
        (data) => prayerUpdater({ data, prayerId, type, response }),
      )
    },
    onMutate: ({ prayerId }) => {
      if (
        type === MESSAGE_TYPE.THANKS_INTENTION_PRAYER ||
        type === MESSAGE_TYPE.THANKS_BIRTHDAY_INTENTION_PRAYER ||
        type === MESSAGE_TYPE.THANKS_ARRIVAL_INTENTION_PRAYER
      ) {
        queryClient.setQueryData(
          getIntentionPrayersOptions(intentionId, messageTypePrayerTypeMap[type]).queryKey,
          (data) => prayerUpdater({ data, prayerId, type }),
        )
      }
    },
  })
}
